import { red } from '@mui/material/colors';
import React, { useEffect, useState } from 'react';
import {
  CreateVehicleSmartInsightResponse,
  VehicleSmartInsightsTip,
  VehicleSmartInsightUserCategory,
  VehicleSmartInsightUserCategoryType,
} from '@/api/requests/llm/interfaces';
import { useLLM } from '@/hooks/useLLM';
import { Box, Divider, Icons, Stack, Text } from '@/theme/index';
import { SmartInsightUserTipsSkeleton } from './SmartInsightUserTipsSkeleton/SmartInsightUserTipsSkeleton';
import { StyledBox, StyledStack } from './components';

interface SmartInsightUserTipsProps {
  currentUserCategoryType: VehicleSmartInsightUserCategoryType;
}

export function SmartInsightUserTips({
  currentUserCategoryType,
}: SmartInsightUserTipsProps) {
  const { data: llmResponse, isLoading } = useLLM('vehicle');
  const typedLLMResponse = llmResponse as CreateVehicleSmartInsightResponse;
  const [currentUserCategory, setCurrentUserCategory] = useState<
    VehicleSmartInsightUserCategory | undefined
  >(typedLLMResponse?.data?.userCategories[currentUserCategoryType]);

  useEffect(() => {
    if (typedLLMResponse?.data?.userCategories) {
      setCurrentUserCategory(
        typedLLMResponse?.data?.userCategories[currentUserCategoryType]
      );
    }
  }, [typedLLMResponse, currentUserCategoryType]);

  if (isLoading || !currentUserCategory) {
    return <SmartInsightUserTipsSkeleton />;
  }

  const addIngToVerb = (verb: string) => {
    return `${verb[0].toUpperCase() + verb.slice(1)}ing`;
  };

  const emoji = () => {
    switch (currentUserCategory.rank) {
      case 1: {
        return (
          <Icons.SentimentVeryDissatisfied
            sx={{
              color: red[400],
              mr: { xs: 0, sm: '0.75rem' },
              mb: { xs: '0.75rem', sm: 0 },
            }}
          />
        );
      }
      case 2: {
        return (
          <Icons.SentimentSatisfied
            sx={{
              color: 'warning.light',
              mr: { xs: 0, sm: '0.75rem' },
              mb: { xs: '0.75rem', sm: 0 },
            }}
          />
        );
      }
      case 3: {
        return (
          <Icons.SentimentVerySatisfied
            sx={{
              color: 'success.light',
              mr: { xs: 0, sm: '0.75rem' },
              mb: { xs: '0.75rem', sm: 0 },
            }}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  const headerText = () => {
    switch (currentUserCategory.rank) {
      case 1: {
        return 'Concerning';
      }
      case 2: {
        return 'Satisfactory';
      }
      case 3: {
        return 'Excellent';
      }
      default: {
        return null;
      }
    }
  };

  const tip = (tipData: VehicleSmartInsightsTip, index: number) => {
    return (
      <StyledStack flexDirection="row" key={tipData.header}>
        <Text px="0.375rem" justifyContent="center">
          {index + 1}
        </Text>
        <Stack pl={1}>
          <Stack>
            <Text variant="h4" mt={0}>
              {tipData.header}
            </Text>
          </Stack>
          <Text>{tipData.detail}</Text>
        </Stack>
      </StyledStack>
    );
  };

  return (
    <StyledBox data-testid="SmartInsightUserTips">
      <Stack flexDirection={{ xs: 'column', sm: 'row' }}>
        {emoji()}
        <Box mb={3} flexGrow={1}>
          <Text variant="h3">{headerText()}</Text>
          {currentUserCategory.justification}
        </Box>
      </Stack>
      <Divider sx={{ borderColor: '#FFFFFF1A', mb: '1.5rem' }} />
      <Stack>
        <Stack flexDirection="row">
          <Icons.LightbulbSharp
            sx={{ color: 'warning.light', mr: 1, width: '1.5rem' }}
            fontSize="small"
          />
          <Text mt={0.25} variant="h4">
            Tips When {addIngToVerb(currentUserCategoryType)}
          </Text>
        </Stack>
        {currentUserCategory.tips?.map((tipData, index) => tip(tipData, index))}
      </Stack>
    </StyledBox>
  );
}
